import { useCallback } from 'react'

import { useRole } from 'Pods/ItemPage/TopSection/hooks/useTopSectionProps'
import useApiDelete from 'Pods/Shared/Hooks/useApiDelete'
import useApiGet from 'Pods/Shared/Hooks/useApiGet'
import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import useApiPut from 'Pods/Shared/Hooks/useApiPut'
import useNotifications from 'Pods/Shared/Hooks/useNotifications'

import useUseCaseLogger from './useUseCaseLogger'
import useUseCaseTranslate from './useUseCaseTranslate'
import { transformEmbeds } from '../Utils/embedTransformer'
import { parseValidationErrors } from '../Utils/errorMessages'
import { buildOptimisticEmbeds } from '../Utils/optimisticEmbeds'

/**
 * Custom hook for managing use case actions (create, update, delete, like/unlike, etc.).
 *
 * @param {Object} params - Hook parameters
 * @param {Object[]} params.useCases - Currently loaded use cases rendered in the sidebar.
 * @param {string} params.currentUsername - Username of the authenticated user.
 * @param {string} params.role - Current user role, affects notifications.
 * @param {number|string} params.productId - The product/item ID for API calls.
 * @param {Function} params.showLocalNotification - Callback to surface local (sidebar) notifications.
 * @param {Function} params.buildFormData - Factory that converts the pending media/options into FormData for POST/PUT.
 * @param {Function} params.clearPendingMedia - Resets local pending media state after successful submission.
 * @param {Function} params.getFormValues - Gets current form values for logging and submission.
 * @param {Function} params.resetForm - Clears the form fields after successful submission.
 * @param {Function} params.handleAdd - Insert/restore a use case in local state.
 * @param {Function} params.handleRemove - Remove a use case from local state.
 * @param {Function} params.handleUpdate - Update a use case in local state (likes, status, etc.).
 * @param {Function} [params.onCreateSuccess] - Optional callback invoked after the API confirms creation.
 */
export default function useUseCaseActions({
  useCases,
  currentUsername,
  productId,
  showLocalNotification,
  clearPendingMedia,
  buildFormData,
  getFormValues,
  resetForm,
  handleAdd,
  handleRemove,
  handleUpdate,
  onCreateSuccess,
}) {
  const translator = useUseCaseTranslate()
  const { t, tv } = translator
  const role = useRole()
  const { deleteData } = useApiDelete()
  const { getData } = useApiGet()
  const { postData } = useApiPost()
  const { putData, isLoading: isUpdating } = useApiPut()
  const { logError } = useUseCaseLogger(role, currentUsername)
  const { showSuccess, showError } = useNotifications()

  const handleMediaPost = async () => {
    const values = getFormValues()

    const formDataMetadata = {
      pendingMediaCount: values.pendingMedia?.length || 0,
      mediaSources: values.pendingMedia?.map(({ source }) => source).filter(Boolean) || [],
      hasSlicerFile: Boolean(values.slicerFile),
      technologyOption: values.technology?.value ?? values.technology ?? null,
      printerTypeId: values.printerType?.printerTypeId ?? values.printerType?.value ?? null,
      printerTypeName: values.printerType?.printerTypeName ?? values.printerType?.label ?? null,
      material: values.material?.value ?? values.material ?? null,
      printSuccess: values.printSuccess?.value ?? values.printSuccess ?? null,
      printTime: values.printTime?.value ?? values.printTime ?? null,
      usedSupport: values.usedSupport ?? false,
    }

    // Build FormData
    let formData
    try {
      formData = buildFormData()
    } catch (error) {
      logError('creation_build_form_data_failed', error, formDataMetadata)
      showLocalNotification(t('errors.prepareForm'), 'error')
      return
    }

    // Clear form immediately (sidebar closes after successful API response)
    clearPendingMedia()
    resetForm()

    // Show processing message (global notification that persists after sidebar closes)
    if (window.showNotification) {
      window.showNotification(
        tv('status.posting'),
        {
          type: 'success',
          autohide: 5000,
        }
      )
    } else {
      showSuccess(tv('status.posting'))
    }

    postData({
      path: `/api/internal/items/${productId}/item_use_cases/`,
      data: formData,
      onSuccess: () => {
        // Close sidebar after successful creation
        if (typeof onCreateSuccess === 'function') {
          onCreateSuccess()
        }

        // Show success notification after server confirms (global notification)
        if (window.showNotification) {
          if (role === 'seller') {
            window.showNotification(
              tv('status.posted'),
              {
                type: 'success',
                autohide: 5000,
              }
            )
          } else {
            window.showNotification(
              tv('status.pendingReview'),
              {
                type: 'success',
                autohide: 7000,
              }
            )
          }
        } else if (role === 'seller') {
          showSuccess(tv('status.posted'))
        } else {
          showSuccess(tv('status.pendingReview'))
        }
      },
      onError: (error) => {
        const { status } = error.response || {}

        // Logging
        logError('creation_api_failed', error, {
          httpStatus: status,
          ...formDataMetadata,
        })

        // Show error notification after server responds (global notification)
        if (window.showNotification) {
          if (status === 422) {
            const errorMessage = parseValidationErrors(error, translator)
            window.showNotification(errorMessage, {
              type: 'error',
              autohide: 7000,
            })
          } else {
            window.showNotification(
              tv('errors.post'),
              {
                type: 'error',
                autohide: 5000,
              }
            )
          }
        } else if (status === 422) {
          showError(parseValidationErrors(error, translator))
        } else {
          showError(tv('errors.post'))
        }
      },
    })
  }

  const fetchPrinterTypes = useCallback(({ onSuccess, onError } = {}) => {
    getData({
      path: '/api/internal/printer_types',
      onSuccess,
      onError,
    })
  }, [getData])

  const handleLike = (itemId, useCaseId) => {
    const currentUseCase = useCases.find(useCase => useCase.id === useCaseId)

    // Early bailout
    if (currentUseCase?.isLoading || !itemId || !useCaseId) {
      return
    }

    // Set loading state and optimistic update
    handleUpdate(useCaseId, {
      isLoading: true,
      liked: true,
      likeCount: currentUseCase.likeCount + 1,
    })

    const likeEndpoint = `/api/internal/items/${itemId}/item_use_cases/${useCaseId}/like`

    postData({
      path: likeEndpoint,
      data: {},
      onSuccess: () => {
        handleUpdate(useCaseId, { isLoading: false })
      },
      onError: (error) => {
        logError('like_failed', error, {
          useCaseId,
          productId: itemId,
          previousLikeCount: currentUseCase.likeCount,
          httpStatus: error?.response?.status,
        })

        // Rollback optimistic update to original state
        handleUpdate(useCaseId, {
          isLoading: false,
          liked: false,
          likeCount: currentUseCase.likeCount,
        })
      },
    })
  }

  const handleUnlike = (itemId, useCaseId) => {
    const currentUseCase = useCases.find(useCase => useCase.id === useCaseId)

    // Early bailout
    if (currentUseCase?.isLoading || !itemId || !useCaseId) {
      return
    }

    // Set loading state and optimistic update
    handleUpdate(useCaseId, {
      isLoading: true,
      liked: false,
      likeCount: Math.max(0, currentUseCase.likeCount - 1),
    })

    const unlikeEndpoint = `/api/internal/items/${itemId}/item_use_cases/${useCaseId}/like`

    deleteData({
      path: unlikeEndpoint,
      onSuccess: () => {
        handleUpdate(useCaseId, { isLoading: false })
      },
      onError: (error) => {
        logError('unlike_failed', error, {
          useCaseId,
          productId: itemId,
          previousLikeCount: currentUseCase.likeCount,
          httpStatus: error?.response?.status,
        })

        // Rollback optimistic update to original state
        handleUpdate(useCaseId, {
          isLoading: false,
          liked: true,
          likeCount: currentUseCase.likeCount,
        })
      },
    })
  }

  const handleUseCaseApproval = async (useCaseId) => {
    const originalUseCase = useCases.find(uc => uc.id === useCaseId)

    handleUpdate(useCaseId, { status: 'accepted' })

    const formData = new FormData()
    formData.append('item_use_case[status]', 'accepted')

    putData({
      path: `/api/internal/items/${productId}/item_use_cases/${useCaseId}`,
      data: formData,
      onSuccess: () => {
        showLocalNotification(
          tv('status.approved'),
          'success'
        )
      },
      onError: (error) => {
        logError('approval_failed', error, {
          useCaseId,
          previousStatus: originalUseCase?.status,
          httpStatus: error?.response?.status,
        })

        if (originalUseCase) {
          handleUpdate(useCaseId, { status: originalUseCase.status })
        }

        showLocalNotification(
          tv('errors.approve'),
          'error'
        )
      },
    })
  }

  const handleUseCaseDeletion = async (useCaseId) => {
    const originalUseCase = useCases.find(uc => uc.id === useCaseId)
    if (!originalUseCase) return

    handleRemove(useCaseId)

    deleteData({
      path: `/api/internal/items/${productId}/item_use_cases/${useCaseId}`,
      onSuccess: () => {
        showLocalNotification(
          tv('status.deleted'),
          'success'
        )
      },
      onError: (error) => {
        logError('deletion_failed', error, {
          useCaseId,
          httpStatus: error?.response?.status,
        })

        handleAdd(originalUseCase)
        showLocalNotification(
          tv('errors.delete'),
          'error'
        )
      },
    })
  }

  const handleUseCaseRejection = async (useCaseId) => {
    const originalUseCase = useCases.find(uc => uc.id === useCaseId)
    if (!originalUseCase) return

    handleRemove(useCaseId)

    const formData = new FormData()
    formData.append('item_use_case[status]', 'rejected')

    putData({
      path: `/api/internal/items/${productId}/item_use_cases/${useCaseId}`,
      data: formData,
      onSuccess: () => {
        showLocalNotification(
          tv('status.rejected'),
          'success'
        )
      },
      onError: (error) => {
        logError('rejection_failed', error, {
          useCaseId,
          previousStatus: originalUseCase.status,
          httpStatus: error?.response?.status,
        })

        handleAdd(originalUseCase)
        showLocalNotification(
          tv('errors.reject'),
          'error'
        )
      },
    })
  }

  const handleUseCaseUpdate = async (
    useCaseId,
    technologyValue,
    printerTypeId,
    printerTypeName,
    materialValue,
    printSuccessValue,
    descriptionValue,
    printTimeValue,
    usedSupportValue,
    onComplete,
    onError,
    mediaChanges = { added: [], removed: [] }
  ) => {
    const originalUseCase = useCases.find(({ id }) => id === useCaseId)
    if (!originalUseCase) return

    // Optimistic update
    const date = new Date().toISOString()

    const optimisticEmbeds = buildOptimisticEmbeds({
      originalEmbeds: originalUseCase.useCaseEmbeds,
      mediaChanges,
      date,
    })

    const optimisticUpdate = {
      technology: technologyValue,
      printerType: printerTypeId && printerTypeName ? {
        id: printerTypeId,
        name: printerTypeName,
        createdAt: originalUseCase.printerType?.createdAt || date,
        updatedAt: date,
        hidden: originalUseCase.printerType?.hidden || false,
        manufacturerId: originalUseCase.printerType?.manufacturerId || null,
      } : null,
      material: materialValue || null,
      printSuccess: printSuccessValue || null,
      description: descriptionValue || null,
      printTime: printTimeValue || null,
      usedSupport: usedSupportValue !== undefined ? usedSupportValue : false,
      useCaseEmbeds: optimisticEmbeds,
    }

    handleUpdate(useCaseId, optimisticUpdate)

    const formData = new FormData()
    if (technologyValue) {
      formData.append('item_use_case[technology]', technologyValue)
    }
    if (printerTypeId) {
      formData.append('item_use_case[printer_type_id]', printerTypeId)
    }
    if (materialValue) {
      formData.append('item_use_case[material]', materialValue)
    }
    if (printSuccessValue) {
      formData.append('item_use_case[print_success]', printSuccessValue)
    }
    if (descriptionValue?.trim()) {
      formData.append('item_use_case[description]', descriptionValue.trim())
    }
    if (printTimeValue) {
      formData.append('item_use_case[print_time]', printTimeValue)
    }
    if (usedSupportValue !== undefined && usedSupportValue !== null) {
      formData.append('item_use_case[used_support]', usedSupportValue.toString())
    }

    // Handle media changes (nested attributes for embeds)
    let embedAttributeIndex = 0

    // Add removed embeds (with _destroy: true)
    mediaChanges.removed.forEach((embedId) => {
      formData.append(`item_use_case[use_case_embeds_attributes][${embedAttributeIndex}][id]`, embedId)
      formData.append(`item_use_case[use_case_embeds_attributes][${embedAttributeIndex}][_destroy]`, 'true')
      embedAttributeIndex += 1
    })

    // Add new embeds
    mediaChanges.added.forEach((media) => {
      if (media.source === 'upload' && media.file) {
        // File upload
        formData.append(
          `item_use_case[use_case_embeds_attributes][${embedAttributeIndex}][embed_url]`,
          media.file
        )
        formData.append(
          `item_use_case[use_case_embeds_attributes][${embedAttributeIndex}][source]`,
          'upload'
        )
      } else if (media.externalId) {
        // External embed (YouTube, Vimeo, etc.)
        formData.append(
          `item_use_case[use_case_embeds_attributes][${embedAttributeIndex}][external_id]`,
          media.externalId
        )
        formData.append(
          `item_use_case[use_case_embeds_attributes][${embedAttributeIndex}][source]`,
          media.source
        )
      }
      formData.append(
        `item_use_case[use_case_embeds_attributes][${embedAttributeIndex}][position]`,
        embedAttributeIndex
      )
      embedAttributeIndex += 1
    })

    putData({
      path: `/api/internal/items/${productId}/item_use_cases/${useCaseId}`,
      data: formData,
      onSuccess: (response) => {
        // Update with complete server response to reflect all changes immediately
        if (response?.data?.item_use_case) {
          const serverUseCase = response.data.item_use_case

          // Transform use_case_embeds from server (snake_case) to client format (camelCase)
          const mappedEmbeds = transformEmbeds(serverUseCase.use_case_embeds)

          // Map server response (snake_case) to client format (camelCase)
          handleUpdate(useCaseId, {
            technology: serverUseCase.technology,
            printerType: serverUseCase.printer_type ? {
              id: serverUseCase.printer_type.id,
              name: serverUseCase.printer_type.name,
              createdAt: serverUseCase.printer_type.created_at,
              updatedAt: serverUseCase.printer_type.updated_at,
              hidden: serverUseCase.printer_type.hidden,
              manufacturerId: serverUseCase.printer_type.manufacturer_id,
            } : null,
            material: serverUseCase.material,
            printSuccess: serverUseCase.print_success,
            description: serverUseCase.description,
            printTime: serverUseCase.print_time,
            usedSupport: serverUseCase.used_support,
            useCaseEmbeds: mappedEmbeds,
          })
        }
        showLocalNotification(
          tv('status.updated'),
          'success'
        )

        if (onComplete) onComplete()
      },
      onError: (error) => {
        logError('update_failed', error, {
          useCaseId,
          previousTechnology: originalUseCase.technology,
          previousPrinterType: originalUseCase.printerType?.id,
          httpStatus: error?.response?.status,
        })

        // Rollback optimistic update
        handleUpdate(useCaseId, {
          technology: originalUseCase.technology,
          printerType: originalUseCase.printerType,
          material: originalUseCase.material,
          printSuccess: originalUseCase.printSuccess,
          description: originalUseCase.description,
          printTime: originalUseCase.printTime,
          usedSupport: originalUseCase.usedSupport,
          useCaseEmbeds: originalUseCase.useCaseEmbeds,
        })
        showLocalNotification(
          tv('errors.update'),
          'error'
        )

        if (onError) onError()
      },
    })
  }

  return {
    handleLike,
    handleMediaPost,
    handleUnlike,
    handleUseCaseApproval,
    handleUseCaseDeletion,
    handleUseCaseRejection,
    handleUseCaseUpdate,
    fetchPrinterTypes,
    isUpdating,
  }
}
